import {
  Box,
  Skeleton,
  TableCell,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { supportApi } from "../client.js";
import { KpiCard } from "../components/KpiCard.js";
import { SupportErrorState } from "../components/SupportEmptyState.js";
import { SupportTable } from "../components/SupportTable.js";
import type { SupportReportPoint } from "../types.js";

/**
 * Reports — KPI strip up top, then volume sparkline, SLA-attainment
 * line, and an agent leaderboard. Range selector flips the queryKey so
 * caches are kept per window. Charts are SVG-only (no recharts/d3) to
 * keep the bundle tight.
 */
export function ReportsPage(): JSX.Element {
  const theme = useTheme();
  const [range, setRange] = useState<"7d" | "30d" | "90d">("30d");

  const summaryQuery = useQuery({
    queryKey: ["support", "report", "summary", range],
    queryFn: () => supportApi.reportSummary(range),
    staleTime: 60_000,
  });
  const volumeQuery = useQuery({
    queryKey: ["support", "report", "volume", range],
    queryFn: () => supportApi.reportVolume(range),
    staleTime: 60_000,
  });
  const slaQuery = useQuery({
    queryKey: ["support", "report", "sla", range],
    queryFn: () => supportApi.reportSla(range),
    staleTime: 60_000,
  });
  const agentsQuery = useQuery({
    queryKey: ["support", "report", "agents", range],
    queryFn: () => supportApi.reportAgents(range),
    staleTime: 60_000,
  });

  return (
    <Box component="section" sx={{ display: "grid", gap: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
        <RangeSelect value={range} onChange={setRange} />
      </Box>

      {summaryQuery.isLoading && <Skeleton variant="rounded" sx={{ height: 120 }} />}
      {summaryQuery.isError && (
        <SupportErrorState
          error={summaryQuery.error}
          resourceLabel="report summary"
        />
      )}
      {summaryQuery.data?.data && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: 1.5 }}>
          <KpiCard
            label="Open tickets"
            value={summaryQuery.data.data.open_tickets}
          />
          <KpiCard
            label="Unassigned"
            value={summaryQuery.data.data.unassigned_tickets}
            tone={
              summaryQuery.data.data.unassigned_tickets > 0 ? "warn" : "good"
            }
          />
          <KpiCard
            label="Breaching SLA"
            value={summaryQuery.data.data.breaching_sla}
            tone={summaryQuery.data.data.breaching_sla > 0 ? "bad" : "good"}
          />
          <KpiCard
            label="Resolved this period"
            value={summaryQuery.data.data.resolved_last_period}
            tone="good"
          />
          {typeof summaryQuery.data.data.median_first_response_min === "number" && (
            <KpiCard
              label="Median first response"
              value={`${summaryQuery.data.data.median_first_response_min}m`}
            />
          )}
          {typeof summaryQuery.data.data.median_resolution_min === "number" && (
            <KpiCard
              label="Median resolution"
              value={`${formatHours(summaryQuery.data.data.median_resolution_min)}`}
            />
          )}
          {typeof summaryQuery.data.data.csat_score === "number" && (
            <KpiCard
              label="CSAT"
              value={`${summaryQuery.data.data.csat_score}/100`}
              tone={summaryQuery.data.data.csat_score >= 80 ? "good" : "warn"}
            />
          )}
        </Box>
      )}

      <ChartCard title="Volume" subtitle="Tickets created per day">
        {volumeQuery.isLoading && <Skeleton variant="rounded" sx={{ height: 160 }} />}
        {volumeQuery.isError && (
          <SupportErrorState
            error={volumeQuery.error}
            resourceLabel="volume"
          />
        )}
        {volumeQuery.data && (
          <Sparkline points={volumeQuery.data.data} stroke={theme.palette.primary.main} />
        )}
      </ChartCard>

      <ChartCard
        title="SLA attainment"
        subtitle="Percentage of tickets resolved within SLA, per day"
      >
        {slaQuery.isLoading && <Skeleton variant="rounded" sx={{ height: 160 }} />}
        {slaQuery.isError && (
          <SupportErrorState error={slaQuery.error} resourceLabel="SLA data" />
        )}
        {slaQuery.data && (
          <Sparkline points={slaQuery.data.data} stroke={theme.palette.success.main} suffix="%" />
        )}
      </ChartCard>

      <ChartCard
        title="Agent performance"
        subtitle="Tickets resolved and average resolution time per agent"
      >
        {agentsQuery.isLoading && <Skeleton variant="rounded" sx={{ height: 120 }} />}
        {agentsQuery.isError && (
          <SupportErrorState
            error={agentsQuery.error}
            resourceLabel="agent performance"
          />
        )}
        {agentsQuery.data && (
          <SupportTable
            head={
              <TableRow>
                <TableCell align="left">Agent</TableCell>
                <TableCell align="left">Resolved</TableCell>
                <TableCell align="left">Open</TableCell>
                <TableCell align="left">Avg resolution</TableCell>
                <TableCell align="left">CSAT</TableCell>
              </TableRow>
            }
            empty={
              agentsQuery.data.data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6, color: "text.disabled" }}>
                    No agent activity in this range.
                  </TableCell>
                </TableRow>
              ) : null
            }
          >
            {agentsQuery.data.data.map((row) => (
              <TableRow
                key={row.user.id}
                sx={{ borderTop: 1, borderColor: "divider", "&:hover": { bgcolor: "action.hover" } }}
              >
                <TableCell sx={{ fontWeight: 500 }}>{row.user.name}</TableCell>
                <TableCell>{row.resolved}</TableCell>
                <TableCell>{row.open}</TableCell>
                <TableCell>{formatHours(row.avg_resolution_min)}</TableCell>
                <TableCell>
                  {typeof row.csat_score === "number"
                    ? `${row.csat_score}/100`
                    : "—"}
                </TableCell>
              </TableRow>
            ))}
          </SupportTable>
        )}
      </ChartCard>
    </Box>
  );
}

/**
 * Lightweight card for chart-shaped content. Border-only, no shadow,
 * generous padding so the chart breathes.
 */
function ChartCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Box
      component="article"
      sx={{
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (t) => t.shadows[1],
        p: 2.5,
      }}
    >
      <Box component="header" sx={{ mb: 2 }}>
        <Typography component="h2" sx={{ fontSize: "1rem", fontWeight: 600, letterSpacing: "-0.01em", color: "text.primary" }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: 14, color: "text.disabled" }}>{subtitle}</Typography>
      </Box>
      {children}
    </Box>
  );
}

function RangeSelect({
  value,
  onChange,
}: {
  value: "7d" | "30d" | "90d";
  onChange: (v: "7d" | "30d" | "90d") => void;
}): JSX.Element {
  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      onChange={(_e, v) => { if (v) onChange(v as "7d" | "30d" | "90d"); }}
      sx={{
        borderRadius: 999,
        bgcolor: "surfaceSoft",
        p: 0.25,
        "& .MuiToggleButton-root": {
          border: 0,
          borderRadius: 999,
          px: 1.5,
          py: 0.5,
          fontSize: 12,
          textTransform: "none",
          color: "text.disabled",
          "&:hover": { color: "text.primary", bgcolor: "transparent" },
          "&.Mui-selected": {
            bgcolor: "background.paper",
            color: "text.primary",
            fontWeight: 500,
            boxShadow: (t) => t.shadows[1],
            "&:hover": { bgcolor: "background.paper" },
          },
        },
      }}
    >
      {(["7d", "30d", "90d"] as const).map((r) => (
        <ToggleButton key={r} value={r}>
          {r}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

function Sparkline({
  points,
  stroke,
  suffix = "",
}: {
  points: SupportReportPoint[];
  stroke: string;
  suffix?: string;
}): JSX.Element {
  if (points.length === 0) {
    return (
      <Typography sx={{ px: 0.5, py: 6, textAlign: "center", fontSize: 14, color: "text.disabled" }}>
        No data in this range.
      </Typography>
    );
  }
  const W = 800;
  const H = 160;
  const PAD = 24;
  const max = Math.max(...points.map((p) => p.value), 1);
  const stepX = (W - PAD * 2) / Math.max(points.length - 1, 1);
  const path = points
    .map((p, i) => {
      const x = PAD + i * stepX;
      const y = H - PAD - (p.value / max) * (H - PAD * 2);
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const areaPath =
    `${path} L${(PAD + (points.length - 1) * stepX).toFixed(1)},${H - PAD} L${PAD.toFixed(1)},${H - PAD} Z`;
  return (
    <Box sx={{ width: "100%" }}>
      <Box
        component="svg"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="trend"
        sx={{ width: "100%" }}
      >
        <defs>
          <linearGradient id="sparkArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity="0.25" />
            <stop offset="100%" stopColor={stroke} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#sparkArea)" />
        <path d={path} fill="none" stroke={stroke} strokeWidth="2" />
      </Box>
      <Box sx={{ mt: 1, display: "flex", justifyContent: "space-between", fontSize: 12, color: "text.disabled" }}>
        <span>{points[0]?.date}</span>
        <span>
          peak {max}
          {suffix}
        </span>
        <span>{points[points.length - 1]?.date}</span>
      </Box>
    </Box>
  );
}

function formatHours(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.round(minutes / 6) / 10;
  return `${h}h`;
}
